// The live document in the editor, kept honest: it edits only while the file
// may be edited (useLiveEditState) and otherwise remounts read only — a new
// WOPI token — under a banner that says why. "No access" (403/404) gets a
// plain explanation instead of the raw token error.
//
// onStateChange({mode, reason, lock}) fires once the state is known and on
// every change; onError({code, status}) when the file can't be opened.

import { useCallback, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { FileStateBanner } from '@/shared/ui/FileStateBanner.jsx';
import { fileStateText } from '@/shared/utils/fileStateText.js';
import { isLockedByMe } from '@/shared/utils/nodeAccess.js';
import { CollaboraEditor } from './index.jsx';
import { EditorNoAccess } from './EditorNoAccess.jsx';
import { useLiveEditState } from './useLiveEditState.js';
import { failureCode, isNoAccess } from './liveState.js';

export function LiveEditor({ fileUuid, requested = 'edit', onStateChange, onError, ...editorProps }) {
  const { t } = useTranslation('editor');
  const live = useLiveEditState(fileUuid, { requested });
  const { ready, mode, reason, lock, failure, fault, reportFailure, refresh } = live;
  const onStateRef = useRef(onStateChange);
  const onErrorRef = useRef(onError);
  useEffect(() => { onStateRef.current = onStateChange; }, [onStateChange]);
  useEffect(() => { onErrorRef.current = onError; }, [onError]);

  // The state, once known and on every change (again after an access gap).
  const reportedRef = useRef(null);
  useEffect(() => {
    if (failure) {
      reportedRef.current = null;
      return;
    }
    if (!ready) return;
    const key = `${mode}:${reason ?? ''}`;
    const previous = reportedRef.current;
    if (previous === key) return;
    reportedRef.current = key;
    onStateRef.current?.({ mode, reason: reason ?? null, lock });
    if (previous?.startsWith('edit:') && mode === 'view') {
      toast.info(t('block.editingStopped', {
        why: fileStateText(t, reason, lock, { mine: isLockedByMe({ lock }) }),
      }));
    }
  }, [ready, failure, mode, reason, lock, t]);

  useEffect(() => {
    if (failure) onErrorRef.current?.(failure);
  }, [failure]);
  useEffect(() => {
    if (fault) onErrorRef.current?.(fault);
  }, [fault]);

  const handleLoadError = useCallback((info) => {
    const code = failureCode(info?.status ?? null);
    if (code === 'error') onErrorRef.current?.({ code, status: info?.status ?? null });
    else reportFailure(info);
  }, [reportFailure]);

  if (isNoAccess(failure)) return <EditorNoAccess reason={failure.reason} onRetry={refresh} />;

  if (!ready) {
    return (
      <div className="collabora-container">
        <div className="collabora-loading">
          <div className="collabora-spinner" />
          <span>{t('loading')}</span>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full min-h-0 flex-1 flex-col">
      <FileStateBanner reason={reason} lock={lock} />
      <div className="flex min-h-0 flex-1 flex-col">
        <CollaboraEditor
          key={mode}
          fileUuid={fileUuid}
          readonly={mode === 'view'}
          onLoadError={handleLoadError}
          {...editorProps}
        />
      </div>
    </div>
  );
}
